import React, { useState } from 'react';
import { TextareaAutosize } from '@mui/material';
import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '../../../hooks/useTranslation';
import StyledErrorMessage from '../Input/StyledErrorMessage';

interface TextareaProps {
  disabled?: boolean;
  fullWidth?: boolean;
  initialValue?: string;
  label?: string;
  name: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  required?: boolean;
  withError?: boolean;
  helperText?: string;
  minRows?: number;
  maxRows?: number;
  maxLength?: number;
}

// Styled component to customize the TextareaAutosize
const StyledTextarea = styled(TextareaAutosize)`
  font-family: inherit;
  font-size: 0.875rem;
  padding: 12px;
  border: 1px solid #b3b3b3;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.2s ease;
  resize: vertical;

  &:hover {
    border-color: #b3b3b3;
  }

  &:focus {
    border-color: #b3b3b3;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.08);
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #bdbdbd;
    cursor: not-allowed;
  }

  ${({ style }) =>
    style &&
    `${Object.entries(style)
      .map(([k, v]) => `${k}: ${v}`)
      .join(';')}`}
`;

const TextareaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const TextareaLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #333;
`;

const Textarea: React.FC<TextareaProps> = ({
  disabled = false,
  fullWidth = true,
  initialValue = '',
  label,
  name,
  onChange,
  placeholder,
  required = false,
  withError = false,
  helperText,
  minRows = 3,
  maxRows = 10,
  maxLength,
  ...props
}): JSX.Element => {
  const { register, watch } = useFormContext();
  const [value, setValue] = useState<string>(initialValue);
  const watchedValue = watch(name) ?? '';
  const { t } = useTranslation();

  const defaultHelperText = helperText || t('This is a required field');

  return (
    <TextareaContainer>
      {label && !withError && <TextareaLabel>{label}</TextareaLabel>}
      {label && withError && (
        <TextareaLabel style={{ color: 'var(--error-text-color)' }}>
          {label}
        </TextareaLabel>
      )}
      <StyledTextarea
        disabled={disabled}
        minRows={minRows}
        maxRows={maxRows}
        placeholder={placeholder}
        value={watchedValue || value}
        {...register(name, {
          required,
          onChange: event => setValue(event.target.value as string),
          maxLength,
        })}
        {...props}
      />
      {withError && (
        <StyledErrorMessage>{defaultHelperText}</StyledErrorMessage>
      )}
      {maxLength && (
        <span style={{ fontSize: '0.75rem', color: '#999' }}>
          {(watchedValue || value).length} / {maxLength}
        </span>
      )}
    </TextareaContainer>
  );
};

export default Textarea;
